import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { isPage, isSection } from "../../../../common/utils";
import { FormControl } from "react-bootstrap";
import IdealStudio from "../../../../core/editor/main";
import i18n from "i18next";

import "./_editorIndexTitle.scss";

/**
 * Get default value if left empty
 * @returns {string}
 */
function getDefaultValue(props) {
  if (isPage(props.id)) {
    return i18n.t("page");
  } else if (isSection(props.id)) {
    return i18n.t("section");
  }
  return "Blank";
}

/**
 * Component for editing index elements in situ
 */
export default class EditorIndexTitle extends Component {
  /**
   * Constructor
   * @param props
   */
  constructor(props) {
    super(props);
    /**
     * Component's initial state
     */
    this.state = {
      editing: false,
      secondClick: this.props.selected === this.props.id,
      currentValue:
        this.props.courseTitle && this.props.title === undefined
          ? i18n.t("Title_document")
          : this.props.title,
      originalTitle: this.props.title || "",
    };
    this.titleIndexRef = createRef();
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    return (
      (this.props.selected === this.props.id && nextProps.selected !== this.props.id) ||
      (this.props.selected !== this.props.id && nextProps.selected === this.props.id) ||
      this.props.title !== nextProps.title ||
      this.props.courseTitle !== nextProps.courseTitle ||
      this.props.hidden !== nextProps.hidden ||
      (this.state.editing && !nextState.editing) ||
      (!this.state.editing && nextState.editing) ||
      (this.state.editing && !nextState.editing && nextProps.selected !== nextProps.id) ||
      this.state.currentValue !== nextState.currentValue ||
      this.state.secondClick !== nextState.secondClick
    );
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!this.state.editing) {
      if (this.props.courseTitle && this.props.title !== prevProps.title) {
        this.setState({
          currentValue: this.props.title,
          originalTitle: this.props.title || "",
        });
      } else if (!this.props.courseTitle && this.props.title !== prevProps.title) {
        this.setState({
          currentValue: this.props.title || "",
          originalTitle: this.props.title || "",
        });
      }
    }

    if (
      !this.state.editing &&
      prevProps.selected !== prevProps.id &&
      this.props.selected === this.props.id
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ secondClick: false });
    }

    if (
      this.state.editing &&
      prevProps.selected === prevProps.id &&
      this.props.selected !== this.props.id
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ secondClick: false });
    }
  }

  startEditing() {
    if (!this.state.editing) {
      this.setState({ editing: true });
      if (this.props.onEditingChange) {
        this.props.onEditingChange(true);
      }
    }
  }

  saveTitle() {
    if (this.state.editing) {
      // Save the title first
      if (this.props.courseTitle) {
        this.props.onNameChanged("title", this.state.currentValue);
      } else {
        this.props.onNameChanged(
          this.props.id,
          this.state.currentValue.length > 0
            ? { viewName: this.state.currentValue }
            : this.getDefaultValue()
        );
      }

      // Exit editing mode immediately
      this.setState({ editing: false }, () => {
        // Blur the input field after state update
        if (this.titleIndexRef.current) {
          this.titleIndexRef.current.blur();
        }
      });

      if (this.props.onEditingChange) {
        this.props.onEditingChange(false);
      }
    }
  }

  /**
   * Renders React Component
   * @returns {code}
   */
  render() {
    const titleStyle = this.props.courseTitle
      ? {
          fontFamily: "Inter, sans-serif",
          fontWeight: "bold",
          fontSize: "12px",
          color: "#454ed8",
          lineHeight: "16px",
          letterSpacing: "-0.06px",
          cursor: "text",
        }
      : {};

    const isSelected = this.props.selected === this.props.id;
    const selectedClass = isSelected ? "selected" : "";

    return (
      <span
        id={this.props.id}
        className={`editor-index-title ${selectedClass}`}
        style={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          height: "100%",
        }}
      >
        {!this.state.editing ? (
          <div
            className={`actualSectionTitle ${selectedClass}`}
            id={"title_" + this.props.id}
            style={{
              textDecoration: this.props.hidden ? "line-through" : "initial",
              cursor:
                this.props.selected === this.props.id || this.props.courseTitle
                  ? "text"
                  : "pointer",
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
              ...titleStyle,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (
                (this.state.secondClick &&
                  !this.state.editing &&
                  this.props.selected === this.props.id) ||
                this.props.courseTitle
              ) {
                this.setState({
                  editing: true,
                  originalTitle: this.props.title || "",
                  currentValue: this.props.title || "",
                });
                if (this.props.onEditingChange) {
                  this.props.onEditingChange(true);
                }
              } else {
                this.setState({ secondClick: true });
              }
            }}
            onDoubleClick={(e) => {
              const newEditingState = !this.state.editing;
              if (newEditingState) {
                this.setState({
                  editing: newEditingState,
                  originalTitle: this.props.title || "",
                  currentValue: this.props.title || "",
                });
              } else {
                this.setState({ editing: newEditingState });
                if (this.state.editing) {
                  this.props.onNameChanged(this.props.id, { viewName: this.state.currentValue });
                }
              }
              if (this.props.onEditingChange) {
                this.props.onEditingChange(newEditingState);
              }
              e.stopPropagation();
            }}
          >
            <span className="actualSectionTitleText">
              {IdealStudio.Config.show_numbers_before_navitems ? this.props.index : ""}{" "}
              {this.props.title && this.props.title !== ""
                ? this.props.title
                : this.props.courseTitle
                  ? i18n.t("Title_document")
                  : i18n.t("Page")}
            </span>
          </div>
        ) : (
          <FormControl
            type="text"
            ref={this.titleIndexRef}
            placeholder={this.props.courseTitle ? i18n.t("Title_document") : i18n.t("Page")}
            className={`${this.props.courseTitle ? "editTitle" : "editSectionTitle"} ${selectedClass}`}
            value={this.state.currentValue}
            autoFocus
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                // Enter Key
                this.setState({ editing: !this.state.editing });
                if (this.props.courseTitle) {
                  this.props.onNameChanged("title", this.state.currentValue);
                } else {
                  this.props.onNameChanged(
                    this.props.id,
                    this.state.currentValue.length > 0
                      ? { viewName: this.state.currentValue }
                      : this.getDefaultValue()
                  );
                }
                if (this.props.onEditingChange) {
                  this.props.onEditingChange(!this.state.editing);
                }
              }
              if (e.keyCode === 27) {
                // Escape key
                this.setState({ editing: !this.state.editing });
                if (this.props.onEditingChange) {
                  this.props.onEditingChange(!this.state.editing);
                }
              }
            }}
            onFocus={(e) => {
              /* Select all the content when enter edition mode*/
              e.target.setSelectionRange(0, e.target.value.length);
            }}
            onChange={(e) => {
              /* Save it on component state, not Redux*/
              this.setState({ currentValue: e.target.value });
            }}
            onBlur={(e) => {
              if (this.state.editing) {
                this.setState({ editing: false });
                if (this.props.courseTitle) {
                  this.props.onNameChanged("title", this.state.currentValue);
                } else {
                  const trimmedValue = (this.state.currentValue || "").trim();
                  if (trimmedValue.length > 0) {
                    this.props.onNameChanged(this.props.id, { viewName: trimmedValue });
                  } else {
                    this.props.onNameChanged(this.props.id, {
                      viewName: this.state.originalTitle || "",
                    });
                  }
                }
                if (this.props.onEditingChange) {
                  this.props.onEditingChange(false);
                }
              }

              e.preventDefault();
              e.stopPropagation();
            }}
          />
        )}
      </span>
    );
  }

  /**
   * Get default value if left empty
   * @returns {string}
   */
  getDefaultValue() {
    if (isPage(this.props.id)) {
      return i18n.t("page");
    } else if (isSection(this.props.id)) {
      return i18n.t("section");
    }
    return "Blank";
  }
}

EditorIndexTitle.propTypes = {
  /**
   * Selected index identifier
   */
  id: PropTypes.string,
  /**
   * Selected index title
   */
  title: PropTypes.string,
  /**
   * Selected index number
   */
  index: PropTypes.any,
  /**
   * Private navItem indicator (show/hide in visor and exported)
   */
  hidden: PropTypes.bool,
  /**
   * Callback for changing page title
   */
  onNameChanged: PropTypes.func.isRequired,
  /**
   * Course title
   */
  courseTitle: PropTypes.any,
  /**
   * Selected navItem
   */
  selected: PropTypes.string,
  /**
   * Callback for when editing state changes
   */
  onEditingChange: PropTypes.func,
};
